import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Papa from "papaparse";
import L from "leaflet";

/* =======================
   DOT ICON (outside component)
======================= */
const createDotIcon = () =>
  L.divIcon({
    className: "dot-icon",
    html: `<div class="dot"></div>`,
  });

/* =======================
   STOP WORDS + HELPERS
======================= */
const STOP_WORDS = new Set([
  "a", "an", "the", "and", "or", "of", "in", "on", "at", "to", "for", "by", "with", "from",
]);

function cleanText(text = "") {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(" ")
    .filter((word) => word && !STOP_WORDS.has(word))
    .join(" ");
}

function buildQuery(item) {
  return [
    cleanText(item["Name of Incident"]),
    item.year,
    cleanText(item.country),
    cleanText(item.place),
    cleanText(item.type),
  ]
    .filter(Boolean)
    .join(" ");
}

async function getImageUrl(partialUrl) {
  const query =
    "https://en.wikipedia.org/w/api.php?action=query&prop=pageimages&format=json&piprop=original&origin=*&titles=";

  try {
    const response = await fetch(`${query}${partialUrl}`);
    if (!response.ok) throw new Error("Network response was not ok");
    const data = await response.json();
    const pages = data.query.pages;
    for (const page of Object.values(pages)) {
      if (page.original && page.original.source) {
        return page.original.source;
      }
    }
  } catch (error) {
    console.error("Error fetching image:", error);
    return null;
  }
  return null;
}

/* =======================
   WIKIPEDIA FETCH (ON CLICK ONLY)
======================= */
const wikiCache = new Map();

async function fetchWikipedia(query) {
  if (wikiCache.has(query)) return wikiCache.get(query);

  try {
    const url = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(
      query
    )}&format=json&origin=*`;

    const res = await fetch(url);
    const data = await res.json();

    const result = data?.query?.search?.[0];
    if (!result) return null;

    const wikiUrl = `https://en.wikipedia.org/wiki/${encodeURIComponent(result.title)}`;
    const wikiImg = await getImageUrl(result.title);

    const wikiData = { url: wikiUrl, img: wikiImg };
    wikiCache.set(query, wikiData);
    return wikiData;
  } catch (err) {
    console.error("Wikipedia api error:", err);
    return null;
  }
}

/* =======================
   CLEAN UNKNOWN VALUES  ← YOUR FIX
======================= */
const clean = (val) => {
  if (!val) return "";
  const removed = val.replace(/Unknown/g, "").trim();
  return removed.toLowerCase() === "unknown" ? "" : removed;
};

export default function MapView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(2026);
  const [wikiMap, setWikiMap] = useState({});
  const [loadingWiki, setLoadingWiki] = useState({});

  /* =======================
     LOAD CSV DATA
  ======================= */
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await fetch(
          "/data/historical_events_geocoded.csv"
        );
        const csvText = await res.text();

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            const mapped = result.data
              .map((item, index) => {
                const lat = parseFloat(item.latitude);
                const lon = parseFloat(item.longitude);

                if (isNaN(lat) || isNaN(lon)) return null;

                return {
                  id: item["Sl. No"] || index,
                  title: clean(item["Name of Incident"]) || "Untitled Event",
                  year: parseInt(item.Year),
                  date: [item.Date, item.Month, item.Year]
                    .filter(val => val && val.trim().toLowerCase() !== "unknown")
                    .join(" "),
                  country: clean(item.Country),
                  type: clean(item["Type of Event"]),
                  place: clean(item["Place Name"]),
                  impact: clean(item.Impact),
                  population: clean(item["Affected Population"]),
                  responsible: clean(item["Important Person/Group Responsible"]),
                  outcome: clean(item.Outcome),
                  position: [lat, lon],
                };
              })
              .filter(Boolean);

            setEvents(mapped);
            setLoading(false);
          },
        });
      } catch (err) {
        console.error("Error loading CSV:", err);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  /* =======================
     WIKIPEDIA ON CLICK
  ======================= */
  const handleMarkerClick = async (event) => {
    if (wikiMap[event.id] || loadingWiki[event.id]) return;

    setLoadingWiki((prev) => ({ ...prev, [event.id]: true }));

    const query = buildQuery({
      "Name of Incident": event.title,
      year: event.year,
    });

    const entry = await fetchWikipedia(query);

    setWikiMap((prev) => ({ ...prev, [event.id]: entry }));
    setLoadingWiki((prev) => ({ ...prev, [event.id]: false }));
  };

  /* =======================
     FILTER DATA
  ======================= */
  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.title
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesYear = event.year && event.year <= year;
    return matchesSearch && matchesYear;
  });

  if (loading) return <p>Loading map...</p>;

  /* =======================
     UI
  ======================= */
  return (
    <div style={{ width: "100vw", height: "100vh", margin: 0, padding: 0 }}>
      {/* SEARCH BOX */}
      <div style={styles.searchBox}>
        <input
          type="text"
          placeholder="Search historical events..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.input}
        />
      </div>

      {/* TIMELINE SLIDER */}
      <div style={styles.sliderBox}>
        <label>Year : {year} </label>
        <input
          type="range"
          min="1800"
          max="2026"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ width: "250px" }}
        />
      </div>

      {/* MAP */}
      <MapContainer
        center={[20, 0]}
        zoom={3}
        minZoom={2}
        style={{
          position: "fixed",
          top: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 0,
          background: "#003a7b",
        }}
      >
        <TileLayer
          url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
          noWrap={true}
        />

        {/* MARKERS */}
        {filteredEvents.map((event) => (
          <Marker
            key={event.id}
            position={event.position}
            icon={createDotIcon()}
            eventHandlers={{
              click: () => handleMarkerClick(event),
            }}
          >
            <Popup>
              <div style={{ maxWidth: "250px" }}>
                <h3 style={{ marginBottom: "6px" }}>
                  <a
                    href={wikiMap[event.id]?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1a73e8",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    {event.title}
                  </a>
                </h3>
                {wikiMap[event.id]?.img && (
                  <img src={wikiMap[event.id]?.img} width={"250px"} alt={event.title} />
                )}
                <p><b>Date:</b> {event.date}</p>
                {event.country     && <p><b>Country:</b> {event.country}</p>}
                {event.type        && <p><b>Type:</b> {event.type}</p>}
                {event.place       && <p><b>Place:</b> {event.place}</p>}
                {event.impact      && <p><b>Impact:</b> {event.impact}</p>}
                {event.population  && <p><b>Affected:</b> {event.population}</p>}
                {event.responsible && <p><b>Responsible:</b> {event.responsible}</p>}
                {event.outcome     && <p><b>Outcome:</b> {event.outcome}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

/* =======================
   STYLES
======================= */
const styles = {
  searchBox: {
    position: "absolute",
    top: 10,
    left: 50,
    zIndex: 1000,
    background: "white",
    padding: "8px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  input: {
    width: "250px",
    padding: "8px",
    border: "none",
    outline: "none",
    fontSize: "14px",
  },
  sliderBox: {
    position: "absolute",
    top: 70,
    left: 50,
    zIndex: 1000,
    background: "white",
    padding: "8px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
};